// Interpolated text for templates: `Batch {{ lot.number }} · {{ receiptDate | date("YYYY-MM-DD") }}`.
// The expression inside the braces is the flow guard language (core `evaluate`: paths,
// arithmetic, comparisons, lists), followed by optional filters joined with `|`. Filters are
// the one place a template formats a value; they are a fixed list the editor offers, never
// user code. A missing path is null and prints as an empty string, so a template never
// throws at print time — the editor warns instead (`checkText`).
import { evaluate, ExprError } from "@trail/core";
const FILTERS = {
    upper: (v) => str(v).toUpperCase(),
    lower: (v) => str(v).toLowerCase(),
    trim: (v) => str(v).trim(),
    /** Date formatting of an ISO date or timestamp: YYYY, YY, MM, DD, HH, mm, WW (ISO week). */
    date: (v, [fmt]) => formatDate(str(v), typeof fmt === "string" ? fmt : "YYYY-MM-DD"),
    /** The arrival week as the owner writes it: YY/WW. */
    week: (v) => formatDate(str(v), "YY/WW"),
    /** Number with `n` decimals, Swedish decimal comma; integers stay without decimals when n is omitted. */
    number: (v, [n]) => {
        const x = typeof v === "number" ? v : Number(String(v ?? "").replace(",", "."));
        if (!Number.isFinite(x))
            return str(v);
        if (typeof n === "number")
            return x.toFixed(n).replace(".", ",");
        return Number.isInteger(x) ? String(x) : String(Math.round(x * 1000) / 1000).replace(".", ",");
    },
    /** Left-pad with a character to a length: `{{ no | pad(6, "0") }}`. */
    pad: (v, [n, ch]) => str(v).padStart(typeof n === "number" ? n : 0, typeof ch === "string" && ch ? ch : " "),
    /** Cut to `n` characters, with an ellipsis when cut. */
    truncate: (v, [n]) => { const s = str(v); const len = typeof n === "number" ? n : 20; return s.length > len ? `${s.slice(0, Math.max(0, len - 1))}…` : s; },
    /** First `n` characters, no ellipsis. */
    left: (v, [n]) => str(v).slice(0, typeof n === "number" ? n : 1),
    right: (v, [n]) => str(v).slice(-(typeof n === "number" ? n : 1)),
    /** Replacement when the value is null or empty. */
    default: (v, [d]) => (v === null || v === "" ? (d ?? "") : v),
    /** Join a list: `{{ products.sku | join(", ") }}`. */
    join: (v, [sep]) => (Array.isArray(v) ? v.map((x) => str(x)).join(typeof sep === "string" ? sep : ", ") : str(v)),
    /** A list as lines (one per row) — an address block. */
    lines: (v) => (Array.isArray(v) ? v.map((x) => str(x)).join("\n") : str(v)),
    /** Text before/after the value, only when the value is not empty. */
    prefix: (v, [p]) => (v === null || v === "" ? "" : `${str(p ?? "")}${str(v)}`),
    suffix: (v, [p]) => (v === null || v === "" ? "" : `${str(v)}${str(p ?? "")}`),
    count: (v) => (Array.isArray(v) ? v.length : v === null || v === "" ? 0 : 1),
    /** The sum of a list: of a field of each row (`sum('delivered')`), or of the items themselves. */
    sum: (v, [field]) => (Array.isArray(v) ? v.reduce((s, r) => s + (Number(typeof field === "string" && r && typeof r === "object" && !Array.isArray(r) ? r[field] : r) || 0), 0) : Number(v) || 0),
    first: (v) => (Array.isArray(v) ? (v[0] ?? null) : v),
    /** Unit word for a UN/ECE code: MTR → m, KGM → kg, EA → st. */
    unit: (v) => unitWord(str(v)),
    /** `{{ flag | yesno("Ja", "Nej") }}`. */
    yesno: (v, [y, n]) => (v === true || v === "true" || (typeof v === "number" && v !== 0) ? (y ?? "Ja") : (n ?? "Nej")),
    /** SSCC/GTIN digits in readable groups: 3 7350034 150000001 6. */
    groups: (v, [pattern]) => groupDigits(str(v), typeof pattern === "string" ? pattern : ""),
    /** Human-readable GS1 element string: AIs in brackets kept, a space after each. */
    hri: (v) => str(v).replace(/\)(?=\S)/g, ") "),
};
export const FILTER_NAMES = Object.keys(FILTERS);
export function unitWord(uom) {
    return uom === "MTR" ? "m" : uom === "KGM" ? "kg" : uom === "EA" || uom === "PCE" ? "st" : uom === "GRM" ? "g" : uom === "LTR" ? "l" : uom;
}
function str(v) {
    if (v === null || v === undefined)
        return "";
    if (typeof v === "string")
        return v;
    if (typeof v === "number" || typeof v === "boolean")
        return String(v);
    if (Array.isArray(v))
        return v.map((x) => str(x)).join(", ");
    return JSON.stringify(v);
}
function groupDigits(s, pattern) {
    if (!pattern) {
        if (s.length === 18)
            return `${s.slice(0, 1)} ${s.slice(1, 8)} ${s.slice(8, 17)} ${s.slice(17)}`;
        if (s.length === 14)
            return `${s.slice(0, 1)} ${s.slice(1, 8)} ${s.slice(8, 13)} ${s.slice(13)}`;
        return s.replace(/(\d{4})(?=\d)/g, "$1 ");
    }
    const parts = pattern.split(/[\s,-]+/).map(Number).filter((n) => n > 0);
    const out = [];
    let i = 0;
    for (const n of parts) {
        out.push(s.slice(i, i + n));
        i += n;
    }
    if (i < s.length)
        out.push(s.slice(i));
    return out.filter(Boolean).join(" ");
}
export function isoWeek(d) {
    const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const day = t.getUTCDay() || 7;
    t.setUTCDate(t.getUTCDate() + 4 - day);
    const start = Date.UTC(t.getUTCFullYear(), 0, 1);
    return { year: t.getUTCFullYear(), week: Math.ceil(((t.getTime() - start) / 86400000 + 1) / 7) };
}
export function formatDate(iso, fmt) {
    if (!iso)
        return "";
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(iso);
    if (!m)
        return iso;
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    const { year, week } = isoWeek(d);
    const parts = { YYYY: m[1], YY: m[1].slice(2), MM: m[2], DD: m[3], HH: m[4] ?? "", mm: m[5] ?? "", WW: String(week).padStart(2, "0"), WY: String(year).slice(2) };
    return fmt.replace(/YYYY|YY|MM|DD|HH|mm|WW|WY/g, (k) => parts[k] ?? k);
}
/** One `{{ … }}` segment split into the expression and its filters. Filter arguments are
 *  literals (strings, numbers) separated by commas. */
export function parseSegment(src) {
    const parts = [];
    let depth = 0, quote = null, cur = "";
    for (const ch of src) {
        if (quote) {
            cur += ch;
            if (ch === quote)
                quote = null;
            continue;
        }
        if (ch === '"' || ch === "'") {
            quote = ch;
            cur += ch;
            continue;
        }
        if (ch === "(" || ch === "[")
            depth++;
        if (ch === ")" || ch === "]")
            depth--;
        if (ch === "|" && depth === 0) {
            parts.push(cur);
            cur = "";
            continue;
        }
        cur += ch;
    }
    parts.push(cur);
    const [expr, ...rest] = parts;
    const filters = rest.map((f) => {
        const m = /^\s*([a-zA-Z_]\w*)\s*(?:\((.*)\))?\s*$/s.exec(f);
        if (!m)
            throw new ExprError(`bad filter "${f.trim()}"`, 0);
        const args = m[2] ? evaluate(`[${m[2]}]`, {}) : [];
        return { name: m[1], args: Array.isArray(args) ? args : [] };
    });
    return { expr: (expr ?? "").trim(), filters };
}
/** Evaluate one segment to a value. */
export function evalSegment(src, ctx) {
    const { expr, filters } = parseSegment(src);
    let v = expr ? evaluate(expr, ctx) : null;
    for (const f of filters) {
        const fn = FILTERS[f.name];
        if (!fn)
            throw new ExprError(`unknown filter "${f.name}"`, 0);
        v = fn(v, f.args);
    }
    return v;
}
const SEG = /\{\{([\s\S]*?)\}\}/g;
/** Render an interpolated text against the data. Errors become empty segments (print never
 *  fails on a template mistake); `checkText` reports them to the editor. */
export function renderText(text, ctx) {
    return text.replace(SEG, (_, inner) => {
        try {
            return str(evalSegment(inner, ctx));
        }
        catch {
            return "";
        }
    });
}
/** The value of a text that is a single interpolation (a barcode's data, a table's rows). */
export function evalText(text, ctx) {
    const t = text.trim();
    const m = /^\{\{([\s\S]*)\}\}$/.exec(t);
    if (m && !m[1].includes("}}")) {
        try {
            return evalSegment(m[1], ctx);
        }
        catch {
            return null;
        }
    }
    return renderText(text, ctx);
}
/** Problems in an interpolated text: unknown filters, unparsable expressions, paths the data
 *  model does not know (when `known` is given). */
export function checkText(text, known) {
    const problems = [];
    for (const m of text.matchAll(SEG)) {
        const inner = m[1];
        try {
            const { expr, filters } = parseSegment(inner);
            if (expr)
                evaluate(expr, {});
            for (const f of filters)
                if (!FILTERS[f.name])
                    problems.push(`okänt filter "${f.name}"`);
            if (known && expr) {
                for (const p of expr.matchAll(/(?<![\w."'])([a-zA-Z_]\w*(?:\.[a-zA-Z_]\w*)*)/g)) {
                    const path = p[1];
                    if (["true", "false", "null", "in", "row", "sub"].includes(path) || path.startsWith("row.") || path.startsWith("sub."))
                        continue;
                    if (!known.has(path) && !known.has(path.split(".")[0]))
                        problems.push(`okänt fält "${path}"`);
                }
            }
        }
        catch (e) {
            problems.push(e instanceof Error ? e.message : String(e));
        }
    }
    return problems;
}
//# sourceMappingURL=text.js.map